import {
  useEffect,
  useState
} from "react";

import {
  useParams,
  useNavigate
} from "react-router-dom";

import api from "../../api";

import {
  formatMoney
} from "../../utils/formatMoney";

import {
  motion,
  AnimatePresence
} from "framer-motion";

function ClientDetail() {

  const { id } =
    useParams();

  const navigate =
    useNavigate();

  const [client, setClient] =
    useState(null);

  const [sejours, setSejours] =
    useState([]);

  const [selectedSejour, setSelectedSejour] =
    useState(null);

  // =====================
  // FETCH
  // =====================

  useEffect(() => {

    api
      .get(`/clients/${id}`)
      .then((res) =>
        setClient(
          res.data
        )
      );

    api
      .get(
        `/sejours/client/${id}`
      )
      .then((res) =>
        setSejours(
          res.data || []
        )
      )
      .catch(() =>
        setSejours([])
      );

  }, [id]);

  // =====================
  // HELPERS
  // =====================

  const formatDate =
    (date) =>
      date
        ? new Date(
            date
          ).toLocaleDateString()
        : "-";

  const calculReste =
    (s) =>
      Number(
        s.montant_total ||
          0
      ) -
      Number(
        s.total_paye ||
          0
      );

  // =====================
  // STATS
  // =====================

  const totalSejours =
    sejours.length;

  const totalDepense =
    sejours.reduce(
      (
        sum,
        s
      ) =>
        sum +
        Number(
          s.total_paye ||
            0
        ),
      0
    );

  const totalReste =
    sejours.reduce(
      (
        sum,
        s
      ) =>
        sum +
        calculReste(s),
      0
    );

  // =====================
  // LOADING
  // =====================

  if (!client) {

    return (
      <p className="p-4 md:p-6">
        Chargement...
      </p>
    );

  }

  // =====================
  // RENDER
  // =====================

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 min-h-[100dvh] bg-gray-100">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">

        <h2 className="text-2xl md:text-3xl font-bold">
          Fiche Client
        </h2>

        <button
          onClick={() =>
            navigate(-1)
          }
          className="bg-gray-200 hover:bg-gray-300 transition px-4 py-3 rounded-xl"
        >
          ⬅️ Retour
        </button>

      </div>

      {/* INFOS */}
      <div className="bg-white p-4 md:p-6 rounded-2xl shadow">

        <h3 className="font-bold text-xl mb-4 break-words">
          {client.nom}{" "}
          {
            client.post_nom
          }{" "}
          {
            client.prenom
          }
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm md:text-base">

          <p>
            <b>
              Téléphone :
            </b>{" "}
            {
              client.telephone
            }
          </p>

          <p>
            <b>
              Nationalité :
            </b>{" "}
            {
              client.nationalite
            }
          </p>

          <p>
            <b>
              Sexe :
            </b>{" "}
            {
              client.sexe
            }
          </p>

          <p className="break-words">
            <b>
              Adresse :
            </b>{" "}
            {
              client.adresse
            }
          </p>

          <p className="md:col-span-2 break-words">
            <b>
              Pièce :
            </b>{" "}
            {
              client.type_piece
            }{" "}
            -{" "}
            {
              client.numero_piece
            }
          </p>

        </div>

      </div>

      {/* STATS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        <div className="bg-white p-4 rounded-2xl shadow">

          <p className="text-sm text-gray-500">
            Séjours
          </p>

          <p className="text-2xl font-bold mt-2">
            {
              totalSejours
            }
          </p>

        </div>

        <div className="bg-white p-4 rounded-2xl shadow">

          <p className="text-sm text-gray-500">
            Total payé
          </p>

          <p className="text-2xl font-bold text-green-600 mt-2">
            {formatMoney(
              totalDepense
            )}
          </p>

        </div>

        <div className="bg-white p-4 rounded-2xl shadow">

          <p className="text-sm text-gray-500">
            Reste
          </p>

          <p
            className={`text-2xl font-bold mt-2 ${
              totalReste > 0
                ? "text-red-600"
                : "text-green-600"
            }`}
          >
            {formatMoney(
              totalReste
            )}
          </p>

        </div>

      </div>

      {/* MOBILE */}
      <div className="grid gap-3 md:hidden">

        {sejours.length ===
          0 && (

          <div className="bg-white rounded-2xl shadow p-6 text-center text-gray-400">
            Aucun séjour
          </div>

        )}

        {sejours.map((s) => (

          <div
            key={s.id}
            onClick={() =>
              setSelectedSejour(
                s
              )
            }
            className="bg-white rounded-2xl shadow border p-4 cursor-pointer"
          >

            <div className="flex justify-between gap-2 items-start">

              <div>

                <p className="font-semibold">
                  {
                    s.appartement_numero
                  }
                </p>

                <p className="text-xs text-gray-500 mt-1">
                  {formatDate(
                    s.date_debut
                  )}{" "}
                  →{" "}
                  {formatDate(
                    s.date_fin_prevue
                  )}
                </p>

              </div>

              <div className="text-right">

                <p className="text-green-600 font-semibold text-sm">
                  {formatMoney(
                    s.total_paye
                  )}
                </p>

                <p className="text-red-500 font-semibold text-sm">
                  {formatMoney(
                    calculReste(
                      s
                    )
                  )}
                </p>

              </div>

            </div>

          </div>

        ))}

      </div>

      {/* DESKTOP */}
      <div className="hidden md:block bg-white rounded-2xl shadow overflow-x-auto">

        <div className="p-4 border-b font-semibold">
          Historique des séjours
        </div>

        {sejours.length ===
        0 ? (

          <div className="p-6 text-center text-gray-400">
            Aucun séjour
          </div>

        ) : (

          <table className="min-w-full text-sm">

            <thead className="bg-gray-100">

              <tr>

                <th className="px-4 py-3 text-left">
                  Appart
                </th>

                <th className="px-4 py-3 text-center">
                  Début
                </th>

                <th className="px-4 py-3 text-center">
                  Fin
                </th>

                <th className="px-4 py-3 text-right">
                  Payé
                </th>

                <th className="px-4 py-3 text-right">
                  Reste
                </th>

              </tr>

            </thead>

            <tbody>

              {sejours.map(
                (s) => (

                  <tr
                    key={s.id}
                    onClick={() =>
                      setSelectedSejour(
                        s
                      )
                    }
                    className="border-t hover:bg-blue-50 cursor-pointer transition"
                  >

                    <td className="px-4 py-3 font-medium">
                      {
                        s.appartement_numero
                      }
                    </td>

                    <td className="px-4 py-3 text-center">
                      {formatDate(
                        s.date_debut
                      )}
                    </td>

                    <td className="px-4 py-3 text-center">
                      {formatDate(
                        s.date_fin_prevue
                      )}
                    </td>

                    <td className="px-4 py-3 text-right">
                      {formatMoney(
                        s.total_paye
                      )}
                    </td>

                    <td className="px-4 py-3 text-right text-red-500 font-semibold">
                      {formatMoney(
                        calculReste(
                          s
                        )
                      )}
                    </td>

                  </tr>

                )
              )}

              <tr className="bg-gray-100 font-semibold">

                <td
                  colSpan="3"
                  className="px-4 py-3"
                >
                  TOTAL
                </td>

                <td className="px-4 py-3 text-right">
                  {formatMoney(
                    totalDepense
                  )}
                </td>

                <td className="px-4 py-3 text-right text-red-600">
                  {formatMoney(
                    totalReste
                  )}
                </td>

              </tr>

            </tbody>

          </table>

        )}

      </div>

      {/* MODAL */}
      <AnimatePresence>

        {selectedSejour && (

          <motion.div
            className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50 p-3"
            initial={{
              opacity: 0
            }}
            animate={{
              opacity: 1
            }}
            exit={{
              opacity: 0
            }}
            onClick={() =>
              setSelectedSejour(
                null
              )
            }
          >

            <motion.div
              className="bg-white rounded-t-3xl md:rounded-2xl p-5 md:p-6 w-full max-w-md"
              initial={{
                y: 50,
                opacity: 0
              }}
              animate={{
                y: 0,
                opacity: 1
              }}
              exit={{
                y: 50,
                opacity: 0
              }}
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              <h3 className="text-xl font-bold mb-5">
                Détail du séjour
              </h3>

              <div className="space-y-3 text-sm md:text-base">

                <p>
                  <b>
                    Appartement :
                  </b>{" "}
                  {
                    selectedSejour.appartement_numero
                  }
                </p>

                <p>
                  <b>
                    Début :
                  </b>{" "}
                  {formatDate(
                    selectedSejour.date_debut
                  )}
                </p>

                <p>
                  <b>
                    Fin :
                  </b>{" "}
                  {formatDate(
                    selectedSejour.date_fin_prevue
                  )}
                </p>

                <p>
                  <b>
                    Payé :
                  </b>{" "}
                  {formatMoney(
                    selectedSejour.total_paye
                  )}
                </p>

                <p>
                  <b>
                    Total :
                  </b>{" "}
                  {formatMoney(
                    selectedSejour.montant_total
                  )}
                </p>

                <p className="text-red-600 font-semibold">
                  <b>
                    Reste :
                  </b>{" "}
                  {formatMoney(
                    calculReste(
                      selectedSejour
                    )
                  )}
                </p>

              </div>

              <button
                onClick={() =>
                  setSelectedSejour(
                    null
                  )
                }
                className="mt-5 w-full bg-gray-200 hover:bg-gray-300 transition py-3 rounded-xl"
              >
                Fermer
              </button>

            </motion.div>

          </motion.div>

        )}

      </AnimatePresence>

    </div>
  );
}

export default ClientDetail;